import { useEffect, useRef } from 'react';
import { useAchievementsContext } from '@/contexts/AchievementsContext';

/**
 * Centralized achievement trigger logic.
 * Pass in current data counts and this hook auto-checks relevant achievements.
 */
export function useAchievementTriggers(data: {
  transactionCount?: number;
  accountCount?: number;
  recurringCount?: number;
  goalCount?: number;
  categorizedExpenseCount?: number;
  hasIncome?: boolean;
  hasExpense?: boolean;
  hasTransfer?: boolean;
  hasGoalDeposit?: boolean;
  hasCustomCategory?: boolean;
  profileCompleted?: boolean;
}) {
  const { checkAndUnlock } = useAchievementsContext();
  const checkedRef = useRef<Set<string>>(new Set());

  const tryUnlock = (key: string) => {
    if (!checkedRef.current.has(key)) {
      checkedRef.current.add(key);
      checkAndUnlock(key);
    }
  };

  useEffect(() => {
    // First Steps
    if ((data.accountCount ?? 0) >= 1) tryUnlock('first_account');
    if ((data.transactionCount ?? 0) >= 1) tryUnlock('first_transaction');
    if (data.hasIncome) tryUnlock('first_income');
    if (data.hasExpense) tryUnlock('first_expense');
    if (data.hasTransfer) tryUnlock('first_transfer');
    if (data.hasCustomCategory) tryUnlock('first_category');
    if (data.profileCompleted) tryUnlock('profile_completed');

    // Budget Organization
    if ((data.goalCount ?? 0) >= 1) tryUnlock('first_goal');
    if (data.hasGoalDeposit) tryUnlock('first_goal_deposit');
    if ((data.recurringCount ?? 0) >= 1) tryUnlock('first_recurring');

    // Structured budget: 5 transactions + 2 accounts
    if ((data.transactionCount ?? 0) >= 5 && (data.accountCount ?? 0) >= 2)
      tryUnlock('structured_budget');

    // Complete view: 1 account + 1 transaction + 1 categorized
    if (
      (data.accountCount ?? 0) >= 1 &&
      (data.transactionCount ?? 0) >= 1 &&
      (data.categorizedExpenseCount ?? 0) >= 1
    )
      tryUnlock('complete_view');

    // Regularity
    if ((data.transactionCount ?? 0) >= 3) tryUnlock('transactions_3');
    if ((data.transactionCount ?? 0) >= 10) tryUnlock('transactions_10');
    if ((data.transactionCount ?? 0) >= 25) tryUnlock('transactions_25');
    if ((data.transactionCount ?? 0) >= 50) tryUnlock('transactions_50');
    if ((data.transactionCount ?? 0) >= 100) tryUnlock('transactions_100');
    if ((data.recurringCount ?? 0) >= 3) tryUnlock('recurring_3');
    if ((data.goalCount ?? 0) >= 5) tryUnlock('goals_5');

    // Financial Control
    if ((data.categorizedExpenseCount ?? 0) >= 10) tryUnlock('categorized_expenses');
    if ((data.accountCount ?? 0) >= 3) tryUnlock('organized_accounts');

    // Clear vision: 3+ accounts, 20+ transactions, 5+ categorized
    if (
      (data.accountCount ?? 0) >= 3 &&
      (data.transactionCount ?? 0) >= 20 &&
      (data.categorizedExpenseCount ?? 0) >= 5
    )
      tryUnlock('clear_vision');
  }, [
    data.transactionCount,
    data.accountCount,
    data.recurringCount,
    data.goalCount,
    data.categorizedExpenseCount,
    data.hasIncome,
    data.hasExpense,
    data.hasTransfer,
    data.hasGoalDeposit,
    data.hasCustomCategory,
    data.profileCompleted,
  ]);
}

/**
 * Simple hook: trigger an exploration achievement when a page mounts.
 */
export function useExplorationAchievement(key: string) {
  const { checkAndUnlock } = useAchievementsContext();
  const triggered = useRef(false);

  useEffect(() => {
    if (!triggered.current) {
      triggered.current = true;
      checkAndUnlock(key);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
